/**
 * Projectors for Education & Experience (the unified timeline) and Bar Admission.
 *
 * WHAT MAKES THIS RISKY
 *
 * The timeline is a single chronological array of SIX different entry shapes
 * discriminated on `kind`, and it is the section that absorbed three retired ones
 * (Legal Education, Other Education, Eligibility Preserving Activities). Two
 * things go wrong easily:
 *
 *  1. Flattening it. Reading the array as though every entry had the same fields
 *     loses most of the content — a clerkship has a judge and a court type, an
 *     undergrad degree has majors and honors, and neither has the other's fields.
 *     Every `kind` therefore gets its own field list, and adding a seventh kind to
 *     the union fails to compile here until it is handled.
 *  2. Losing the identifying name. Which school or court an entry refers to is
 *     nested differently in every kind, and for a U.S. law school it hides behind
 *     a "not listed" sentinel: `schoolSelection` holds the sentinel and the real
 *     name sits in `schoolName`. Reading `schoolSelection` blindly renders the
 *     sentinel string as the school name.
 *
 * ORDER IS DERIVED, NOT STORED. Entries are sorted by start date here because the
 * timeline's own contract is that "chronology, gaps, and overlaps are derived";
 * the stored array order is not meaningful. Entries with no start date sort last
 * rather than to 1970, which is where they would land if the blank were coerced.
 */

import type { BarAdmission } from '../../oasis-application.types';
import type {
	ClerkshipEntry,
	FellowshipEntry,
	GraduateLegalEntry,
	IEducationExperienceTimeline,
	InitialLegalDegreeEntry,
	NonLegalGradEntry,
	TimelineEntry,
	UndergradEntry,
} from '../../oasis-timeline.types';
import { TIMELINE_KIND_LABEL } from '../../oasis-timeline.types';
import { US_LAW_SCHOOL_NOT_LISTED } from '../../../reference-data/us-law-schools';
import {
	CLASS_RANK_LABELS,
	CLERKSHIP_COURT_TYPE_LABELS,
	CLERKSHIP_JOB_TITLE_LABELS,
	DOJ_FELLOWSHIP_LABELS,
	EPA_PAYMENT_LABELS,
	EPA_WORK_SCHEDULE_LABELS,
	FELLOWSHIP_TYPE_LABELS,
	GRADUATE_LEGAL_STATUS_LABELS,
	GRADUATION_HONORS_LABELS,
	LAW_DEGREE_PATH_LABELS,
	NON_LEGAL_GRAD_DEGREE_LABELS,
	SLIP_PATHWAYS_ATTRIBUTE_LABELS,
	UNDERGRAD_DEGREE_LABELS,
} from '../oasis-display-labels';
import { formatIsoDateRange } from '../oasis-display-format';
import type { DisplayBlock, DisplayEntry, DisplayField } from '../oasis-display.types';
import {
	dateField,
	dateRangeField,
	definedOnly,
	enumField,
	enumListField,
	field,
	isBlank,
	linkField,
	listField,
	longTextField,
	monthsField,
	textField,
	yesNoField,
} from './field-builders';
import { definedBadges } from './identity.projectors';

// ─── Education & Experience timeline ──────────────────────────────────────────

export function projectEducationExperience(data: IEducationExperienceTimeline): DisplayBlock[] {
	const entries = [...(data.entries ?? [])].sort(byStartDate);

	const blocks: DisplayBlock[] = [
		{
			kind: 'entries',
			entries: entries.map(projectTimelineEntry),
			emptyText: 'No education or experience recorded.',
		},
	];

	// Gap explanations are answers to a question the system asked, so they belong
	// in the section rather than being dropped as derived scaffolding.
	for (const gap of data.gapExplanations ?? []) {
		if (isBlank(gap.explanation)) continue;
		blocks.push({
			kind: 'note',
			heading: `Explanation for gap (${formatIsoDateRange(gap.fromDate, gap.toDate)})`,
			text: gap.explanation,
		});
	}

	return blocks;
}

/**
 * Ascending by start date, with undated entries last.
 *
 * A blank start date must not sort as though it were the epoch: an in-progress
 * row would jump to the top of a reviewer's chronology and read as the
 * applicant's earliest education.
 */
function byStartDate(a: TimelineEntry, b: TimelineEntry): number {
	const aDate = a.startDate?.trim() ?? '';
	const bDate = b.startDate?.trim() ?? '';
	if (!aDate && !bDate) return 0;
	if (!aDate) return 1;
	if (!bDate) return -1;
	return aDate.localeCompare(bDate);
}

function projectTimelineEntry(entry: TimelineEntry): DisplayEntry {
	const base = {
		subtitle: TIMELINE_KIND_LABEL[entry.kind],
	};

	switch (entry.kind) {
		case 'undergrad':
			return { ...base, title: undergradTitle(entry), fields: definedOnly(undergradFields(entry)) };
		case 'non_legal_grad':
			return { ...base, title: nonLegalGradTitle(entry), fields: definedOnly(nonLegalGradFields(entry)) };
		case 'initial_legal_degree':
			return { ...base, title: initialLegalTitle(entry), fields: definedOnly(initialLegalFields(entry)) };
		case 'graduate_legal':
			return { ...base, title: graduateLegalTitle(entry), fields: definedOnly(graduateLegalFields(entry)) };
		case 'clerkship':
			return {
				...base,
				title: entry.courtName?.trim() || TIMELINE_KIND_LABEL.clerkship,
				badges: definedBadges([scheduleBadge(entry.workSchedule), paymentBadge(entry.payment)]),
				fields: definedOnly(clerkshipFields(entry)),
			};
		case 'fellowship':
			return {
				...base,
				title: fellowshipTitle(entry),
				badges: definedBadges([scheduleBadge(entry.workSchedule), paymentBadge(entry.payment)]),
				fields: definedOnly(fellowshipFields(entry)),
			};
	}
}

function scheduleBadge(schedule: string | undefined): string | undefined {
	return schedule === 'full_time' || schedule === 'part_time' ? EPA_WORK_SCHEDULE_LABELS[schedule] : undefined;
}

function paymentBadge(payment: string | undefined): string | undefined {
	return payment === 'paid' || payment === 'unpaid' ? EPA_PAYMENT_LABELS[payment] : undefined;
}

/** Location, written the way a reader expects rather than field by field. */
function locationText(city?: string, stateOrCountry?: string, country?: string): string {
	return [city, stateOrCountry, country]
		.map((p) => p?.trim())
		.filter((p) => !!p)
		.join(', ');
}

/** Common spine fields shared by every kind. */
function spineFields(entry: TimelineEntry): DisplayField[] {
	return [dateRangeField('Dates', entry.startDate, entry.endDate, entry.ongoing), longTextField('Note', entry.note, 'not_applicable')];
}

// ── Undergraduate ──

function undergradTitle(entry: UndergradEntry): string {
	return entry.schoolName?.trim() || TIMELINE_KIND_LABEL.undergrad;
}

function undergradFields(entry: UndergradEntry): DisplayField[] {
	return [
		enumField('Degree', entry.degreeType || undefined, UNDERGRAD_DEGREE_LABELS),
		listField('Majors', entry.majors),
		enumField('Graduation honors', entry.graduationHonors || undefined, GRADUATION_HONORS_LABELS),
		textField('Location', locationText(entry.location?.city, entry.location?.stateOrCountry, entry.location?.country)),
		...spineFields(entry),
	];
}

// ── Non-legal graduate ──

function nonLegalGradTitle(entry: NonLegalGradEntry): string {
	return entry.schoolName?.trim() || TIMELINE_KIND_LABEL.non_legal_grad;
}

function nonLegalGradFields(entry: NonLegalGradEntry): DisplayField[] {
	// `otherDegreeText` carries the real degree when the dropdown said "other",
	// so it wins; showing "Other" alone would discard what the applicant typed.
	const degree =
		entry.degreeType === 'other' && entry.otherDegreeText?.trim()
			? field('Degree', { kind: 'text', value: entry.otherDegreeText.trim() })
			: enumField('Degree', entry.degreeType || undefined, NON_LEGAL_GRAD_DEGREE_LABELS);

	return [
		degree,
		listField('Fields of study', entry.majors),
		enumField('Graduation honors', entry.graduationHonors || undefined, GRADUATION_HONORS_LABELS),
		field('Part of a joint degree with law', { kind: 'text', value: entry.jointDegreeWithLaw ? 'Yes' : 'No' }),
		textField('Location', locationText(entry.location?.city, entry.location?.stateOrCountry, entry.location?.country)),
		...spineFields(entry),
	];
}

// ── Initial legal degree ──

/**
 * Resolves the degree-granting school's name across the three degree paths.
 *
 * For the U.S. J.D. path the name normally comes from the ABA dropdown via
 * `schoolSelection`, but when the school is not on that list the selection holds
 * the `US_LAW_SCHOOL_NOT_LISTED` sentinel and the real name is in `schoolName`.
 * Returning the sentinel would print a placeholder where the school should be.
 */
function degreeConferringName(entry: InitialLegalDegreeEntry): string {
	const school = entry.degreeConferring;
	if (!school) return '';

	if (school.path === 'us_jd') {
		if (school.schoolSelection && school.schoolSelection !== US_LAW_SCHOOL_NOT_LISTED) return school.schoolSelection;
		return school.schoolName?.trim() ?? '';
	}
	return school.schoolName?.trim() ?? '';
}

function initialLegalTitle(entry: InitialLegalDegreeEntry): string {
	return degreeConferringName(entry) || TIMELINE_KIND_LABEL.initial_legal_degree;
}

function initialLegalFields(entry: InitialLegalDegreeEntry): DisplayField[] {
	const school = entry.degreeConferring;
	const fields: DisplayField[] = [
		yesNoField('Has or is enrolled in a J.D. program', entry.hasJd),
		enumField('Degree path', school?.path, LAW_DEGREE_PATH_LABELS),
	];

	if (school?.path === 'us_jd') {
		fields.push(textField('Location', locationText(school.city, school.stateTerritory)));
	} else if (school?.path === 'non_us_jd') {
		fields.push(textField('Location', locationText(school.city, undefined, school.country)));
	} else if (school?.path === 'non_jd') {
		fields.push(
			textField('Location', locationText(school.city, undefined, school.country)),
			textField('Degree type', school.degreeType),
			textField('Bar-eligible jurisdiction', school.barJurisdiction),
		);
	}

	fields.push(
		dateField('Started at degree-granting school', entry.degreeSchoolStartDate),
		dateField('Law degree expected or conferred', entry.lawDegreeExpectedOrConferred),
		textField('GPA', entry.grades?.gpa),
		textField('Grade range', entry.grades?.gradeRange),
		enumField('Class rank', entry.grades?.classRank, CLASS_RANK_LABELS),
	);

	// Transfer origins: schools attended that did not confer the degree.
	const additional = (entry.additionalLawSchools ?? [])
		.map((school) => {
			const span = formatIsoDateRange(school.startDate, school.endDate);
			return span ? `${school.schoolName} (${span})` : school.schoolName;
		})
		.filter((s) => !isBlank(s));
	fields.push(listField('Other law schools attended', additional, 'not_applicable'));

	const achievement = entry.slipPathwaysAchievement;
	if (achievement) {
		fields.push(
			enumListField('Law school record', achievement.attributes, SLIP_PATHWAYS_ATTRIBUTE_LABELS),
			yesNoField('Transcript lists failing or non-passing grades', achievement.hasFailingGrades || undefined),
			yesNoField('Willing to work in Washington, D.C.', achievement.willingToWorkInDC || undefined),
		);
	}

	fields.push(...spineFields(entry));
	return fields;
}

// ── Graduate legal (LLM) ──

function graduateLegalTitle(entry: GraduateLegalEntry): string {
	const school = entry.graduateLegalEducation?.school;
	if (!school) return TIMELINE_KIND_LABEL.graduate_legal;

	if (school.path === 'us') {
		if (school.schoolSelection && school.schoolSelection !== US_LAW_SCHOOL_NOT_LISTED) return school.schoolSelection;
		return school.schoolName?.trim() || TIMELINE_KIND_LABEL.graduate_legal;
	}
	return school.schoolName?.trim() || TIMELINE_KIND_LABEL.graduate_legal;
}

function graduateLegalFields(entry: GraduateLegalEntry): DisplayField[] {
	const gle = entry.graduateLegalEducation;
	const school = gle?.school;
	const fields: DisplayField[] = [yesNoField('Completed or enrolled in an LLM program', gle?.hasLlm)];

	if (school?.path === 'us') {
		fields.push(textField('Location', locationText(school.city, school.stateTerritory)));
	} else if (school?.path === 'non_us') {
		fields.push(
			textField('Location', locationText(school.city, undefined, school.country)),
			field('Joint U.S. / non-U.S. degree recognized', { kind: 'text', value: school.jointDegreeRecognized ? 'Yes' : 'No' }),
		);
	}

	fields.push(enumField('Program status', gle?.statusSelection, GRADUATE_LEGAL_STATUS_LABELS));

	// The LLM block can carry more than one attendance span.
	for (const [index, span] of (gle?.attendance ?? []).entries()) {
		const suffix = (gle?.attendance?.length ?? 0) > 1 ? ` ${index + 1}` : '';
		fields.push(
			dateRangeField(`Attendance${suffix}`, span.startDate, span.endDate),
			dateField(`Degree expected or conferred${suffix}`, span.lawDegreeExpectedOrConferred),
		);
	}

	fields.push(longTextField('Reason duration falls outside 9–12 months', entry.reasonDurationOutOfRange, 'not_applicable'), ...spineFields(entry));
	return fields;
}

// ── Clerkship ──

function clerkshipFields(entry: ClerkshipEntry): DisplayField[] {
	return [
		enumField('Court type', entry.courtType || undefined, CLERKSHIP_COURT_TYPE_LABELS),
		enumField('Job title', entry.jobTitle || undefined, CLERKSHIP_JOB_TITLE_LABELS),
		textField('Judge', [entry.judgeName, entry.judgeTitle].filter((p) => !!p?.trim()).join(', ')),
		textField('Location', locationText(entry.city, entry.stateOrCountry)),
		monthsField('Duration (months)', entry.durationMonths),
		longTextField('Reason under 12 months', entry.reasonUnder12Months, 'not_applicable'),
		longTextField('Duties and responsibilities', entry.dutiesResponsibilities),
		...spineFields(entry),
	];
}

// ── Fellowship ──

function fellowshipTitle(entry: FellowshipEntry): string {
	if (entry.fellowshipName?.trim()) return entry.fellowshipName.trim();
	if (entry.dojFellowshipType) return DOJ_FELLOWSHIP_LABELS[entry.dojFellowshipType];
	if (entry.fellowshipType) return FELLOWSHIP_TYPE_LABELS[entry.fellowshipType];
	return TIMELINE_KIND_LABEL.fellowship;
}

function fellowshipFields(entry: FellowshipEntry): DisplayField[] {
	return [
		enumField('Fellowship type', entry.fellowshipType || undefined, FELLOWSHIP_TYPE_LABELS),
		enumField('DOJ fellowship', entry.dojFellowshipType, DOJ_FELLOWSHIP_LABELS, 'not_applicable'),
		textField('Sponsor organization', entry.sponsorOrganization, 'not_applicable'),
		yesNoField('Sponsor is a private law firm', entry.sponsorIsLawFirm, 'not_applicable'),
		longTextField('Mission or purpose', entry.missionPurpose, 'not_applicable'),
		linkField('Eligibility URL', entry.eligibilityUrl, 'not_applicable'),
		textField('Salary or stipend', entry.salaryStipend, 'not_applicable'),
		textField('Supervisor', [entry.supervisorName, entry.supervisorTitle].filter((p) => !!p?.trim()).join(', ')),
		textField('Location', locationText(entry.city, entry.stateOrCountry)),
		monthsField('Duration (months)', entry.durationMonths),
		longTextField('Reason under 12 months', entry.reasonUnder12Months, 'not_applicable'),
		longTextField('Duties and responsibilities', entry.dutiesResponsibilities),
		...spineFields(entry),
	];
}

// ─── Bar admission ────────────────────────────────────────────────────────────

/**
 * Three-way union on `status`. "Not admitted" IS an answer, not an absence, so it
 * renders as a stated status rather than an empty section — a reviewer needs to
 * see that the question was answered.
 */
export function projectBarAdmission(data: BarAdmission): DisplayBlock[] {
	if (data.status === 'admitted') {
		return [
			{
				kind: 'fields',
				fields: [
					field('Status', { kind: 'text', value: 'Admitted' }),
					listField('Jurisdictions', data.jurisdictions),
					dateField('Date admitted', data.dateAdmitted),
				],
			},
		];
	}

	if (data.status === 'admission_pending') {
		return [
			{
				kind: 'fields',
				fields: [
					field('Status', { kind: 'text', value: 'Admission pending' }),
					listField('Jurisdictions where the bar exam was taken', data.jurisdictions),
					dateField('Expected admission date', data.expectedDate),
				],
			},
		];
	}

	return [{ kind: 'fields', fields: [field('Status', { kind: 'text', value: 'Not admitted' })] }];
}
